import { API_ENDPOINTS, getApiUrl, getAuthHeaders } from '../config/api';
import { buildAdminEventExtraData, safeHttpUrl } from '../utils/adminEventExtraData';
import { mapRawApiEventToEventItem } from '../utils/eventMedia'; 

async function readAdminError(response: Response, fallback: string): Promise<string> {
  const errorData = await response.json().catch(() => ({}));
  const detail = (errorData as { detail?: unknown }).detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => e?.msg ?? '').filter(Boolean).join(', ') || fallback;
  }
  return fallback;
}

export const adminApi = {
  // Dashboard
  getDashboardOverview: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.DASHBOARD.OVERVIEW), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch dashboard overview (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },

  getRevenueChart: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.DASHBOARD.REVENUE), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch revenue data (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },

  getRecentBookings: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.DASHBOARD.RECENT_BOOKINGS), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch recent bookings (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },

  getUpcomingEvents: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.DASHBOARD.UPCOMING_EVENTS), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch upcoming events (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },

  getAllBookings: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.BOOKINGS.BASE), {
        method: 'GET',
        headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch all bookings (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },

  getBookingStats: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.BOOKINGS.STATS), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `Failed to fetch booking stats (${response.status}: ${response.statusText})`,
      );
    }
    return await response.json();
  },

  // User Management
  getUsers: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.USERS.BASE), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch users (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },

  getUserStats: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.USERS.STATS), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch user stats (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },

  // Event Management
  getEvents: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.BASE), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch events (${response.status}: ${response.statusText})`);

    }
    const data = await response.json();
    const eventsList = data.events !== undefined ? data.events : data;
    return { 
      ...data, 
      events: eventsList.map((e: any) => mapRawApiEventToEventItem(e)),
    };
  },

  getEventStats: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.STATS), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch event stats (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },

  createEvent: async (data: any, token?: string) => {
    const payload = {
      title: data.title,
      description: data.description,
      category: data.category,
      venueId: data.venueId,
      eventDate:
        !data.schedule && data.date && data.time
          ? new Date(`${data.date}T${data.time}`).toISOString()
          : undefined,
      scheduleType: data.scheduleType && data.scheduleType !== 'single' ? data.scheduleType : undefined,
      schedule: data.schedule || undefined,
      bookingStartDate: data.bookingStartDate,
      bookingEndDate: data.bookingEndDate,
      bannerImageUrl: safeHttpUrl(data.image),
      organizerName: data.organizerName,
      organizerContact: data.organizerContact,
      termsAndConditions: data.termsAndConditions,
      extraData: buildAdminEventExtraData(data as Record<string, unknown>),
    };
    
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.BASE), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      let errorMsg = errorData.detail || `Failed to create event (${response.status}: ${response.statusText})`;
      if (Array.isArray(errorMsg)) errorMsg = errorMsg.map((e: any) => `${e.loc && e.loc.length > 0 ? e.loc[e.loc.length - 1] + ': ' : ''}${e.msg}`).join(', ') || 'Validation error';
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
    return await response.json();
  },

  publishEvent: async (eventId: string, token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.PUBLISH(eventId)), {
      method: 'POST',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to publish event (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },

  // ---- Schedule / occurrences (multi-slot, multi-day, recurring, season pass) ----

  /** Expand a schedule WITHOUT saving: the dates it would generate + cap warning. */
  previewSchedule: async (
    body: { scheduleType: string; schedule: Record<string, unknown> },
    token?: string
  ): Promise<{
    count: number;
    cap: number;
    overCap: boolean;
    occurrences: { startsAt: string; endsAt?: string | null }[];
  }> => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.SCHEDULE_PREVIEW), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await readAdminError(response, 'Could not preview the schedule'));
    return await response.json();
  },

  listOccurrences: async (eventId: string, token?: string): Promise<any[]> => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.OCCURRENCES(eventId)), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) throw new Error(await readAdminError(response, 'Failed to load occurrences'));
    return await response.json();
  },

  createOccurrence: async (
    eventId: string,
    body: { startsAt: string; endsAt?: string; label?: string },
    token?: string
  ) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.EVENTS.OCCURRENCES(eventId)), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await readAdminError(response, 'Failed to add the date/slot'));
    return await response.json();
  },

  updateOccurrence: async (
    occurrenceId: string,
    body: { startsAt?: string; endsAt?: string | null; label?: string | null; status?: 'scheduled' | 'cancelled' },
    token?: string
  ) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.OCCURRENCES.DETAIL(occurrenceId)), {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await readAdminError(response, 'Failed to update the date/slot'));
    return await response.json();
  },

  /** Deletes an occurrence, or cancels it if it already has bookings. */
  deleteOccurrence: async (occurrenceId: string, token?: string): Promise<{ outcome: string; message: string }> => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.OCCURRENCES.DETAIL(occurrenceId)), {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) throw new Error(await readAdminError(response, 'Failed to remove the date/slot'));
    return await response.json();
  },

  /** Price / capacity of tiers on ONE occurrence; `applyToAll` copies them to every occurrence. */
  updateOccurrenceTiers: async (
    occurrenceId: string,
    body: {
      tiers: { seatCategoryId: number; price?: number; totalSeats?: number; isActive?: boolean }[];
      applyToAll?: boolean;
    },
    token?: string
  ) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.OCCURRENCES.TIERS(occurrenceId)), {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await readAdminError(response, 'Failed to update tier pricing'));
    return await response.json();
  },

  // Venue Management
  getVenues: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.VENUES.ALL), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch venues (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },

  // Promo codes
  listPromoCodes: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.PROMO_CODES.BASE), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch promo codes (${response.status})`);
    }
    return await response.json();
  },

  createPromoCode: async (data: Record<string, unknown>, token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.PROMO_CODES.BASE), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      let msg = errorData.detail || `Failed to create promo code (${response.status})`;
      if (Array.isArray(msg)) msg = msg.map((e: any) => e.msg).join(', ') || 'Validation error';
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    return await response.json();
  },

  updatePromoCode: async (id: string | number, data: Record<string, unknown>, token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.PROMO_CODES.DETAIL(id)), {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to update promo code (${response.status})`);
    }
    return await response.json();
  },

  deletePromoCode: async (id: string | number, token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.ADMIN.PROMO_CODES.DETAIL(id)), {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to delete promo code (${response.status})`);
    }
    return true;
  },

  // Generic admin methods
  get: async (token: string, path: string) => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const response = await fetch(getApiUrl(`/api/admin/${cleanPath}`), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch ${path} (${response.status}: ${response.statusText})`);

    }
    return await response.json();
  },
  
  post: async (token: string, path: string, data: any) => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const response = await fetch(getApiUrl(`/api/admin/${cleanPath}`), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to create ${path} (${response.status}: ${response.statusText})`);
    }
    return await response.json();
  },

  patch: async (token: string, path: string, data: any) => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const response = await fetch(getApiUrl(`/api/admin/${cleanPath}`), {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to update ${path} (${response.status}: ${response.statusText})`);
    }
    return await response.json();
  },

  delete: async (token: string, path: string) => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const response = await fetch(getApiUrl(`/api/admin/${cleanPath}`), {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to delete ${path} (${response.status}: ${response.statusText})`);

    }
    return true;
  }
};
